import { useEffect, useRef } from "react";
import { useUser } from "@webuild-ai/react-auth";

import { ToolPreferences, useToolPreferencesStore } from "@/stores/ToolPreferencesStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { writeUserRecord } from "@/services/userRecordWriter";
import { logger } from "@/telemetry";

const SYNC_DEBOUNCE_MS = 2000;

/**
 * Set equality for one agent's recorded delta. A delta is a membership
 * question, not a write history, so two orderings of the same ids are the same
 * preference and must not trigger a backend sync. Duplicates cannot occur
 * because `withMembership` refuses to add an id already present.
 */
function areToolListsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;

  const bSet = new Set(b);

  return a.every((tool) => bSet.has(tool));
}

/** Structural equality check for ToolPreferences objects (key-order independent). */
function arePreferencesEqual(a: ToolPreferences, b: ToolPreferences): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return false;

  for (const wfId of aKeys) {
    if (!(wfId in b)) return false;

    const aAgents = a[wfId];
    const bAgents = b[wfId];
    const aAgentKeys = Object.keys(aAgents);
    const bAgentKeys = Object.keys(bAgents);

    if (aAgentKeys.length !== bAgentKeys.length) return false;

    for (const agentId of aAgentKeys) {
      if (!(agentId in bAgents)) return false;

      const aPreference = aAgents[agentId];
      const bPreference = bAgents[agentId];

      if (!areToolListsEqual(aPreference.enabled, bPreference.enabled)) return false;
      if (!areToolListsEqual(aPreference.disabled, bPreference.disabled)) return false;
    }
  }

  return true;
}

/**
 * Syncs tool preferences between localStorage (ToolPreferencesStore) and the
 * backend UserConfig.
 *
 * - On first profile load: seeds localStorage from backend preferences (cross-device roaming).
 * - On tool toggle: debounces writes to the backend (2s after last change).
 *
 * The payload is the delta map with the reserved `__version` key stamped onto
 * it, which is `Record<string, Record<string, string[]>>`, the shape the admin
 * API declares for `tool_preferences` (the 2.x SDK sends it without validating). A
 * profile still carrying the pre-delta shape has no marker and is refused by
 * `loadFromBackend` rather than migrated, which is what stops a stale snapshot
 * roaming back and overriding the workflow definition again (LSEG-190,
 * ADR-0026).
 */
export function useToolPreferenceSync(): void {
  const { adminClient } = useWebuildAPIClient();
  const { userProfile } = useUser();
  const preferences = useToolPreferencesStore((s) => s.preferences);
  const bootstrappedUserId = useRef<string | null>(null);
  const lastSyncedPrefs = useRef<ToolPreferences>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Bootstrap: seed localStorage from backend on first profile load (or when user changes)
  useEffect(() => {
    if (!userProfile?.config || bootstrappedUserId.current === userProfile.id) return;
    bootstrappedUserId.current = userProfile.id;

    // Access tool_preferences from backend (may not be in react-auth types yet)
    const backendPrefs =
      (userProfile.config as Record<string, unknown>).toolPreferences ??
      (userProfile.config as Record<string, unknown>).tool_preferences;

    if (backendPrefs) {
      useToolPreferencesStore.getState().loadFromBackend(backendPrefs);
    }

    // Mark the post-bootstrap state as synced so we don't immediately write it back
    lastSyncedPrefs.current = useToolPreferencesStore.getState().preferences;
  }, [userProfile]);

  // Debounced sync to backend after preference changes
  useEffect(() => {
    if (!adminClient || !userProfile) return;

    if (arePreferencesEqual(lastSyncedPrefs.current, preferences)) return;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const state = useToolPreferencesStore.getState();
      // Captured before the await so the ref records what was actually sent,
      // not whatever the store has moved on to by the time the call returns.
      const sent = state.preferences;

      try {
        // The SDK renames `toolPreferences` to `tool_preferences` on the wire. Since
        // `@webuild-ai/admin` 2.0 it no longer strips what it does not model either,
        // so sending both spellings would put two `tool_preferences` keys into the
        // same payload and let one silently overwrite the other.
        //
        // Sent through the shared writer so the body carries the memory setting at
        // its latest value, not as it stood when this profile was captured.
        await writeUserRecord(adminClient, userProfile, {
          toolPreferences: state.getPreferencesForSync(),
        });
        lastSyncedPrefs.current = sent;
        logger.info("Tool preferences synced to backend");
      } catch (error) {
        // This used to separate an outbound validation failure, which is permanent
        // and left roaming silently dead, from a network failure, which is expected
        // occasionally and self-corrects on the next toggle. `@webuild-ai/admin` 2.0
        // removed the zod layer that raised the first: `SDKValidationError` is still
        // exported for compile compatibility but is never thrown, so the branch that
        // tested for it could only ever report a failure this client cannot have.
        // Every failure reaching here is now a transport failure and is logged as one.
        logger.warn("Failed to sync tool preferences to backend", { error: String(error) });
      }
    }, SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [preferences, adminClient, userProfile]);
}
